/**
 * ═══════════════════════════════════════════════════════
 * ELISEE — AI Product Recommender Engine
 * ═══════════════════════════════════════════════════════
 * Client-side recommendation engine using behavioral tracking,
 * content-based filtering, and affinity scoring.
 *
 * API: window.EliseeAI.getRecommendations(context, limit)
 */
(function() {
  'use strict';

  var STORAGE_KEY = 'elisee_ai_profile';
  var MAX_HISTORY = 50;
  var RECENCY_WEIGHT = 0.40;
  var FREQUENCY_WEIGHT = 0.35;
  var SIMILARITY_WEIGHT = 0.25;

  /* ══════════════════════════════
     Team / League Knowledge Base
     ══════════════════════════════ */
  var LEAGUES = {
    'serie-a': {
      name: 'Serie A',
      teams: ['inter', 'milan', 'juventus', 'napoli', 'roma', 'lazio', 'atalanta', 'fiorentina', 'torino', 'bologna', 'genoa', 'sampdoria', 'monza', 'lecce', 'empoli', 'udinese', 'sassuolo', 'verona', 'cagliari', 'como', 'venezia', 'parma', 'palermo', 'catanzaro']
    },
    'serie-b': {
      name: 'Serie B',
      teams: ['benevento', 'reggina', 'bari', 'brescia', 'cosenza', 'catanzaro', 'modena', 'pisa', 'spezia', 'sudtirol', 'feralpisalo', 'ternana', 'ascoli', 'reggiana', 'cittadella', 'cremonese', 'frosinone', 'salernitana']
    },
    'serie-c': {
      name: 'Serie C',
      teams: ['avellino', 'padova', 'vicenza', 'triestina', 'pescara', 'foggia', 'perugia', 'potenza', 'crotone']
    },
    'la-liga': {
      name: 'La Liga',
      teams: ['real-madrid', 'barcellona', 'atletico-madrid']
    },
    'premier-league': {
      name: 'Premier League',
      teams: ['manchester-city', 'liverpool', 'chelsea', 'arsenal', 'manchester-united', 'tottenham']
    },
    'bundesliga': {
      name: 'Bundesliga',
      teams: ['bayern-monaco', 'borussia-dortmund']
    },
    'ligue-1': {
      name: 'Ligue 1',
      teams: ['psg', 'marsiglia']
    },
    'nazionali': {
      name: 'Nazionali',
      teams: ['italia', 'argentina', 'brasile', 'francia', 'spagna', 'germania', 'inghilterra', 'portogallo', 'belgio', 'croazia', 'colombia', 'uruguay', 'giappone', 'scozia', 'svezia', 'svizzera', 'peru']
    }
  };

  /* Rival pairs — if user likes team A, don't recommend team B */
  var RIVALS = {
    'inter': ['milan', 'juventus'],
    'milan': ['inter', 'juventus'],
    'juventus': ['inter', 'torino', 'napoli'],
    'roma': ['lazio'],
    'lazio': ['roma'],
    'napoli': ['juventus'],
    'torino': ['juventus'],
    'genoa': ['sampdoria'],
    'sampdoria': ['genoa'],
    'real-madrid': ['barcellona', 'atletico-madrid'],
    'barcellona': ['real-madrid'],
    'liverpool': ['manchester-united'],
    'manchester-united': ['liverpool', 'manchester-city'],
    'manchester-city': ['manchester-united'],
    'arsenal': ['tottenham'],
    'tottenham': ['arsenal'],
    'psg': ['marsiglia'],
    'marsiglia': ['psg']
  };

  /* ══════════════════════════════
     User Profile Management
     ══════════════════════════════ */
  function getProfile() {
    try {
      var data = localStorage.getItem(STORAGE_KEY);
      if (data) return JSON.parse(data);
    } catch (e) {}
    return {
      views: [],           // { handle, team, league, type, ts }
      cartAdds: [],        // { handle, team, ts }
      teamScores: {},      // { teamHandle: score }
      leagueScores: {},    // { leagueKey: score }
      typePrefs: {},       // { coverType: count }
      lastVisit: Date.now(),
      sessionCount: 0
    };
  }

  function saveProfile(profile) {
    try {
      // Trim history to max
      if (profile.views.length > MAX_HISTORY) {
        profile.views = profile.views.slice(-MAX_HISTORY);
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
    } catch (e) {}
  }

  /* ══════════════════════════════
     Behavioral Tracking
     ══════════════════════════════ */
  function detectTeamFromHandle(handle) {
    if (!handle) return null;
    handle = handle.toLowerCase();
    for (var league in LEAGUES) {
      var teams = LEAGUES[league].teams;
      for (var i = 0; i < teams.length; i++) {
        if (handle.indexOf(teams[i]) !== -1) {
          return { team: teams[i], league: league };
        }
      }
    }
    return null;
  }

  function detectTypeFromTags(tags) {
    if (!tags) return 'standard';
    var t = (Array.isArray(tags) ? tags.join(' ') : tags).toLowerCase();
    if (t.indexOf('magsafe') !== -1) return 'magsafe';
    if (t.indexOf('rigida') !== -1) return 'rigida';
    if (t.indexOf('morbida') !== -1) return 'morbida';
    if (t.indexOf('pellicola') !== -1 || t.indexOf('vetro') !== -1) return 'pellicola';
    if (t.indexOf('laccetto') !== -1 || t.indexOf('accessori') !== -1) return 'accessorio';
    return 'standard';
  }

  function trackView(productHandle, tags, collection) {
    var profile = getProfile();
    var info = detectTeamFromHandle(productHandle) || detectTeamFromHandle(collection);
    var type = detectTypeFromTags(tags);

    var view = {
      handle: productHandle,
      team: info ? info.team : null,
      league: info ? info.league : null,
      type: type,
      ts: Date.now()
    };

    profile.views.push(view);

    // Update team scores
    if (info) {
      if (!profile.teamScores[info.team]) profile.teamScores[info.team] = 0;
      profile.teamScores[info.team] += 1;

      if (!profile.leagueScores[info.league]) profile.leagueScores[info.league] = 0;
      profile.leagueScores[info.league] += 0.5;
    }

    // Update type preferences
    if (!profile.typePrefs[type]) profile.typePrefs[type] = 0;
    profile.typePrefs[type] += 1;

    saveProfile(profile);
  }

  function trackCartAdd(productHandle) {
    var profile = getProfile();
    var info = detectTeamFromHandle(productHandle);
    profile.cartAdds.push({
      handle: productHandle,
      team: info ? info.team : null,
      ts: Date.now()
    });

    // Cart adds are 3x more valuable than views
    if (info) {
      if (!profile.teamScores[info.team]) profile.teamScores[info.team] = 0;
      profile.teamScores[info.team] += 3;
    }

    saveProfile(profile);
  }

  /* ══════════════════════════════
     AI Scoring Algorithm & Preferences
     ══════════════════════════════ */
  function getExplicitPreferences() {
    var prefs = { teams: [] };
    if (typeof window.EliseeCustomerProfile !== 'undefined' && window.EliseeCustomerProfile.note) {
      var note = window.EliseeCustomerProfile.note.toLowerCase();
      // Scan the entire customer note for any team names.
      // This catches both "Squadra Preferita: Milan" and "Luogo di nascita: Napoli" 
      // without needing strict regex parsing, ensuring maximum catch rate.
      for (var league in LEAGUES) {
        var tList = LEAGUES[league].teams;
        for (var i = 0; i < tList.length; i++) {
          if (note.indexOf(tList[i]) !== -1) {
            prefs.teams.push(tList[i]);
          }
        }
      }
    }
    return prefs;
  }

  function computeAffinity() {
    var profile = getProfile();
    var now = Date.now();
    var scores = {};

    // Process all views with recency decay
    for (var i = 0; i < profile.views.length; i++) {
      var v = profile.views[i];
      if (!v.team) continue;

      // Recency: exponential decay over 7 days
      var ageHours = (now - v.ts) / 3600000;
      var recency = Math.exp(-ageHours / 168); // 168h = 7 days

      if (!scores[v.team]) {
        scores[v.team] = { recency: 0, frequency: 0, similarity: 0, league: v.league };
      }
      scores[v.team].recency += recency * RECENCY_WEIGHT;
      scores[v.team].frequency += FREQUENCY_WEIGHT;
    }

    // Process cart adds (higher weight)
    for (var j = 0; j < profile.cartAdds.length; j++) {
      var ca = profile.cartAdds[j];
      if (!ca.team) continue;
      if (!scores[ca.team]) {
        scores[ca.team] = { recency: 0, frequency: 0, similarity: 0, league: null };
      }
      var ageH = (now - ca.ts) / 3600000;
      var rec = Math.exp(-ageH / 168);
      scores[ca.team].recency += rec * RECENCY_WEIGHT * 2;
      scores[ca.team].frequency += FREQUENCY_WEIGHT * 2;
    }

    // Process explicit profile preferences (Registration Data)
    var explicitPrefs = getExplicitPreferences();
    for (var k = 0; k < explicitPrefs.teams.length; k++) {
      var et = explicitPrefs.teams[k];
      if (!scores[et]) {
        scores[et] = { recency: 0, frequency: 0, similarity: 0, league: null };
      }
      // MASSIVE BOOST to override generic behavioral tracking
      scores[et].frequency += 500; 
    }

    // Similarity: same-league bonus
    var topLeague = null;
    var maxLeagueScore = 0;
    for (var lk in profile.leagueScores) {
      if (profile.leagueScores[lk] > maxLeagueScore) {
        maxLeagueScore = profile.leagueScores[lk];
        topLeague = lk;
      }
    }

    if (topLeague) {
      var leagueTeams = LEAGUES[topLeague] ? LEAGUES[topLeague].teams : [];
      for (var lt = 0; lt < leagueTeams.length; lt++) {
        var t = leagueTeams[lt];
        if (!scores[t]) {
           scores[t] = { recency: 0, frequency: 0, similarity: 0, league: topLeague };
        }
        scores[t].similarity += SIMILARITY_WEIGHT * 0.5;
      }
    }

    // Final scoring
    var ranked = [];
    for (var team in scores) {
      var s = scores[team];
      var total = s.recency + s.frequency + s.similarity;
      ranked.push({ team: team, score: total, league: s.league });
    }

    // Sort descending
    ranked.sort(function(a, b) { return b.score - a.score; });

    return ranked;
  }

  /* ══════════════════════════════
     Recommendation Fetcher
     ══════════════════════════════ */
  function fetchCollectionProducts(handle, limit) {
    return fetch('/collections/' + handle + '/products.json?limit=' + (limit || 8))
      .then(function(r) {
        if (!r.ok) throw new Error('Collection not found');
        return r.json();
      })
      .then(function(data) {
        return data.products || [];
      })
      .catch(function() { return []; });
  }

  function getRecommendations(context, limit) {
    limit = limit || 4;
    var profile = getProfile();
    var affinity = computeAffinity();

    // Filter out rivals of top team
    var topTeam = affinity.length > 0 ? affinity[0].team : null;
    var rivalsToExclude = topTeam && RIVALS[topTeam] ? RIVALS[topTeam] : [];

    var filtered = affinity.filter(function(a) {
      return rivalsToExclude.indexOf(a.team) === -1;
    });

    // If context = 'cart', prioritize complementary products (accessories)
    // If context = 'product', prioritize same-team/league alternatives
    // If context = 'homepage', mix of top affinities

    var fetchPromises = [];
    var teamsToFetch = [];

    if (context === 'cart' && topTeam) {
      // For cart: fetch from top team collection (for cross-sell accessories)
      teamsToFetch = [topTeam];
    } else {
      // Get top 3 teams
      for (var i = 0; i < Math.min(filtered.length, 3); i++) {
        teamsToFetch.push(filtered[i].team);
      }
    }

    // Fallback: if no profile data, use popular collections
    if (teamsToFetch.length === 0) {
      teamsToFetch = ['inter', 'milan', 'juventus', 'napoli'];
    }

    for (var j = 0; j < teamsToFetch.length; j++) {
      fetchPromises.push(fetchCollectionProducts(teamsToFetch[j], 6));
    }

    return Promise.all(fetchPromises).then(function(results) {
      var allProducts = [];
      for (var r = 0; r < results.length; r++) {
        for (var p = 0; p < results[r].length; p++) {
          var prod = results[r][p];
          // Don't recommend what user is currently viewing
          if (context === 'product' && window.EliseeAI._currentProduct === prod.handle) continue;
          // Don't duplicate
          var isDupe = false;
          for (var d = 0; d < allProducts.length; d++) {
            if (allProducts[d].id === prod.id) { isDupe = true; break; }
          }
          if (!isDupe) {
            prod._aiScore = (results.length - r) / results.length; // Higher score for higher-affinity team
            allProducts.push(prod);
          }
        }
      }

      // Shuffle within same-score tier for variety
      allProducts.sort(function(a, b) {
        var diff = b._aiScore - a._aiScore;
        if (Math.abs(diff) < 0.01) return Math.random() - 0.5;
        return diff;
      });

      return allProducts.slice(0, limit);
    });
  }

  /* ══════════════════════════════
     Auto-tracking on page load
     ══════════════════════════════ */
  function autoTrack() {
    // Track product page views
    var meta = document.querySelector('meta[property="og:type"][content="og:product"]') ||
               document.querySelector('meta[property="og:type"][content="product"]');
    if (meta || document.querySelector('.product-form__submit')) {
      var handle = window.location.pathname.split('/products/')[1];
      if (handle) {
        handle = handle.split('?')[0].split('#')[0];
        var tags = '';
        var tagEl = document.querySelector('[data-product-tags]');
        if (tagEl) tags = tagEl.getAttribute('data-product-tags');
        var collHandle = '';
        var breadcrumb = document.querySelector('.breadcrumbs a[href*="/collections/"]');
        if (breadcrumb) {
          var href = breadcrumb.getAttribute('href');
          collHandle = href.split('/collections/')[1] || '';
          collHandle = collHandle.split('?')[0].split('/')[0];
        }
        trackView(handle, tags, collHandle);
        window.EliseeAI._currentProduct = handle;
      }
    }

    // Track collection page views
    var collPath = window.location.pathname.match(/\/collections\/([^/?#]+)/);
    if (collPath) {
      var cHandle = collPath[1];
      var info = detectTeamFromHandle(cHandle);
      if (info) {
        var profile = getProfile();
        if (!profile.teamScores[info.team]) profile.teamScores[info.team] = 0;
        profile.teamScores[info.team] += 0.5;
        if (!profile.leagueScores[info.league]) profile.leagueScores[info.league] = 0;
        profile.leagueScores[info.league] += 0.3;
        saveProfile(profile);
      }
    }

    // Track add-to-cart clicks
    document.addEventListener('click', function(e) {
      var btn = e.target.closest('[name="add"], .product-form__submit, #AddToCart');
      if (btn) {
        var handle = window.EliseeAI._currentProduct;
        if (handle) trackCartAdd(handle);
      }
    });
  }

  /* ══════════════════════════════
     Public API
     ══════════════════════════════ */
  window.EliseeAI = {
    getRecommendations: getRecommendations,
    getProfile: getProfile,
    getAffinity: computeAffinity,
    _currentProduct: null
  };

  // Auto-track
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoTrack);
  } else {
    autoTrack();
  }

})();
